import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import {
  BrainCircuit, Send, Sparkles, Loader2, Check,
  MessageSquare, ChevronDown, ArrowRight, X,
  Save, Pin, PinOff, Search, Trash2,
  Edit3, Download, FolderOpen
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { printContent } from '../../lib/exportUtils';
import { API_URL } from './constants';

const Tip = ({ children, text, side = 'top' }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed">{text}</TooltipContent>
  </Tooltip>
);


// ─── CHAT TAB ───────────────────────────────────────────────────────────────
const ChatMessage = ({ message, isUser }) => (
  <div className={cn("flex gap-3 mb-4", isUser && "flex-row-reverse")} data-testid={isUser ? "chat-user-msg" : "chat-cfo-msg"}>
    {!isUser && (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
        <BrainCircuit className="w-4 h-4 text-white" />
      </div>
    )}
    <div className={cn("max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed", isUser ? "bg-emerald-600 text-white rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm")}>
      {message.split('\n').map((line, i) => <p key={i} className={i > 0 ? "mt-2" : ""}>{line}</p>)}
    </div>
  </div>
);

const QUICK_QUESTIONS = [
  "How can I reduce my tax burden this quarter?",
  "Is my cash flow healthy?",
  "Where should I cut expenses?",
  "Should I hire another team member?",
  "What's my break-even point?",
  "How do I improve my profit margin?",
];

function ChatTab({ workspaceId, onSaved, resumeConversation, setResumeConversation, pendingTaxAnalysis, setPendingTaxAnalysis }) {
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [suggestedName, setSuggestedName] = useState('');
  const [saveName, setSaveName] = useState('');
  const [activeConvId, setActiveConvId] = useState(null);
  const [activeConvName, setActiveConvName] = useState(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  // Handle resuming a conversation
  useEffect(() => {
    if (resumeConversation) {
      setChatMessages(resumeConversation.messages || []);
      setActiveConvId(resumeConversation.conversation_id);
      setActiveConvName(resumeConversation.name);
      setResumeConversation(null);
    }
  }, [resumeConversation, setResumeConversation]);

  // Handle pending tax analysis from Scanner tab
  useEffect(() => {
    if (pendingTaxAnalysis) {
      const msg = pendingTaxAnalysis;
      setPendingTaxAnalysis(null);
      // Start a new chat with the tax analysis prompt
      setChatMessages([]);
      setActiveConvId(null);
      setActiveConvName(null);
      // Small delay to ensure the chat tab is rendered
      setTimeout(() => sendMessage(msg), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTaxAnalysis]);

  const sendMessage = async (text = null) => {
    const msg = text || chatInput.trim();
    if (!msg || !workspaceId) return;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: msg }]);
    setChatSending(true);
    try {
      let endpoint, body;
      if (activeConvId) {
        endpoint = `${API_URL}/api/workspaces/${workspaceId}/cfo-buck/conversations/${activeConvId}/resume`;
        body = JSON.stringify({ message: msg });
      } else {
        endpoint = `${API_URL}/api/workspaces/${workspaceId}/cfo-buck/chat`;
        body = JSON.stringify({ message: msg, history: chatMessages.slice(-6) });
      }
      const res = await apiFetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
      if (res.ok) {
        const data = await res.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      }
    } catch { setChatMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I couldn't process that. Please try again." }]); }
    finally { setChatSending(false); inputRef.current?.focus(); }
  };

  const handleSaveClick = async () => {
    if (chatMessages.length === 0) return;
    setSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/cfo-buck/conversations/suggest-name`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: chatMessages }),
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestedName(data.suggested_name || '');
        setSaveName(data.suggested_name || '');
      }
    } catch { setSaveName(chatMessages.find(m => m.role === 'user')?.content?.slice(0, 60) || 'Untitled Chat'); }
    finally { setSaving(false); setShowSaveDialog(true); }
  };

  const confirmSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/cfo-buck/conversations`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: saveName || suggestedName, messages: chatMessages }),
      });
      if (res.ok) {
        const data = await res.json();
        setActiveConvId(data.conversation_id);
        setActiveConvName(data.name);
        setShowSaveDialog(false);
        onSaved?.();
      }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const startNewChat = () => {
    setChatMessages([]);
    setActiveConvId(null);
    setActiveConvName(null);
  };

  const exportPdf = () => {
    const msgHtml = chatMessages.map(m => `
      <div style="margin-bottom:16px;display:flex;gap:12px;${m.role==='user'?'flex-direction:row-reverse':''}">
        <div style="width:32px;height:32px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:white;${m.role==='user'?'background:#059669':'background:linear-gradient(135deg,#8b5cf6,#4f46e5)'}">
          ${m.role==='user'?'You':'CFO'}
        </div>
        <div style="max-width:75%;padding:12px 16px;border-radius:16px;font-size:14px;line-height:1.6;${m.role==='user'?'background:#059669;color:white;border-bottom-right-radius:4px':'background:#f1f5f9;color:#334155;border-bottom-left-radius:4px'}">
          ${m.content.replace(/\n/g,'<br>')}
        </div>
      </div>`).join('');
    printContent(`
      <div style="max-width:700px;margin:0 auto">
        <h1 style="color:#0f172a;font-size:24px;margin:0 0 4px">${activeConvName || 'CFO Buck Conversation'}</h1>
        <p style="color:#94a3b8;font-size:12px;margin:0 0 20px">Exported ${new Date().toLocaleDateString()} from CountBucks</p>
        <hr style="border:1px solid #e2e8f0;margin:0 0 20px">${msgHtml}
        <hr style="border:1px solid #e2e8f0;margin:20px 0">
        <p style="color:#94a3b8;font-size:11px;margin:0">Generated by CFO Buck - AI Financial Officer & Tax Strategist</p>
      </div>
    `, { filename: `CFO_Buck_Chat_${new Date().toISOString().slice(0,10)}.pdf` });
  };

  return (
    <Card className="border-border shadow-sm overflow-hidden" data-testid="cfo-chat-section">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
            <BrainCircuit className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {activeConvName || 'Ask CFO Buck'}
            </h3>
            <p className="text-[11px] text-muted-foreground">
              {activeConvId ? 'Resumed conversation' : 'Get personalized financial and tax advice powered by your data'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {chatMessages.length > 0 && (
            <>
              <Tip text="Export this conversation as a printable PDF to share with your CPA or team.">
                <Button variant="outline" size="sm" className="text-xs" onClick={exportPdf} data-testid="export-chat-btn">
                  <Download className="w-3 h-3 mr-1" />Export
                </Button>
              </Tip>
              {!activeConvId && (
                <Tip text="Save this conversation so you can return to it later. CFO Buck will suggest a name.">
                  <Button variant="outline" size="sm" className="text-xs" onClick={handleSaveClick} disabled={saving} data-testid="save-chat-btn">
                    {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Save Chat
                  </Button>
                </Tip>
              )}
              <Tip text="Start a fresh conversation. Your current chat won't be lost if it's already saved.">
                <Button variant="outline" size="sm" className="text-xs" onClick={startNewChat} data-testid="new-chat-btn">
                  <MessageSquare className="w-3 h-3 mr-1" />New Chat
                </Button>
              </Tip>
            </>
          )}
        </div>
      </div>
      <CardContent className="p-0 relative">
        {/* Save Dialog */}
        {showSaveDialog && (
          <div className="absolute inset-0 z-10 bg-card/90 backdrop-blur-sm flex items-center justify-center p-6" data-testid="save-dialog">
            <div className="w-full max-w-md bg-card rounded-2xl shadow-2xl border border-border p-6">
              <h3 className="text-base font-semibold text-foreground mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Save Conversation</h3>
              <p className="text-xs text-muted-foreground mb-4">CFO Buck suggested a name. Edit it or keep it as-is.</p>
              <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Conversation name..."
                className="mb-4" autoFocus data-testid="save-name-input"
                onKeyDown={(e) => { if (e.key === 'Enter') confirmSave(); }} />
              {suggestedName && saveName !== suggestedName && (
                <button onClick={() => setSaveName(suggestedName)} className="text-[11px] text-violet-500 hover:text-violet-700 mb-4 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />Use AI suggestion: "{suggestedName}"
                </button>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
                <Button size="sm" onClick={confirmSave} disabled={saving}
                  className="bg-gradient-to-r from-violet-500 to-indigo-600 text-white" data-testid="confirm-save-btn">
                  {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Save
                </Button>
              </div>
            </div>
          </div>
        )}
        <div className="h-[420px] overflow-y-auto px-6 py-4" data-testid="chat-messages">
          {chatMessages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500/10 to-indigo-600/10 flex items-center justify-center mb-4">
                <BrainCircuit className="w-6 h-6 text-violet-500" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">What would you like to know?</p>
              <p className="text-xs text-muted-foreground mb-6 max-w-sm">Ask about tax strategy, cash flow, expense optimization, growth decisions, or anything financial.</p>
              <div className="flex flex-wrap justify-center gap-2 max-w-lg">
                {QUICK_QUESTIONS.map((q, i) => (
                  <button key={i} onClick={() => sendMessage(q)} className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground dark:text-muted-foreground hover:border-violet-300 hover:text-violet-600 dark:hover:border-violet-600 dark:hover:text-violet-400 transition-colors" data-testid={`quick-q-${i}`}>{q}</button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {chatMessages.map((msg, i) => <ChatMessage key={i} message={msg.content} isUser={msg.role === 'user'} />)}
              {chatSending && (
                <div className="flex gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0"><BrainCircuit className="w-4 h-4 text-white" /></div>
                  <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </>
          )}
        </div>
        <div className="px-6 py-3 border-t border-border bg-muted/50">
          <div className="flex items-center gap-2">
            <Input ref={inputRef} value={chatInput} onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Ask CFO Buck a question..." disabled={chatSending}
              className="flex-1 h-10 bg-card border-border"
              data-testid="cfo-chat-input" />
            <Button onClick={() => sendMessage()} disabled={!chatInput.trim() || chatSending}
              className="h-10 px-4 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white shadow-sm"
              data-testid="cfo-chat-send"><Send className="w-4 h-4" /></Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5 text-center">CFO Buck uses your real financial data. Always confirm tax strategies with your CPA.</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── SAVED CHATS TAB ────────────────────────────────────────────────────────
const TAG_LABELS = { tax: 'Tax', cash_flow: 'Cash Flow', expenses: 'Expenses', revenue: 'Revenue', hiring: 'Hiring', strategy: 'Strategy', general: 'General' };
const TAG_COLORS = {
  tax: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  cash_flow: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  expenses: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  revenue: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  hiring: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  strategy: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  general: 'bg-muted text-muted-foreground',
};

function SavedChatsTab({ workspaceId, onResume }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedConv, setExpandedConv] = useState(null);
  const [loadingConv, setLoadingConv] = useState(false);

  const fetchConversations = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (filterTag !== 'all') params.set('tag', filterTag);
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/cfo-buck/conversations?${params}`);
      if (res.ok) {
        const d = await res.json();
        setConversations(d.conversations || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [workspaceId, searchQuery, filterTag]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  const handlePin = async (convId, currentPinned) => {
    await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/cfo-buck/conversations/${convId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pinned: !currentPinned }),
    });
    fetchConversations();
  };

  const handleRename = async (convId) => {
    if (!editName.trim()) return;
    await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/cfo-buck/conversations/${convId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: editName }),
    });
    setEditingId(null);
    fetchConversations();
  };

  const handleDelete = async (convId) => {
    await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/cfo-buck/conversations/${convId}`, {
      method: 'DELETE', });
    if (expandedId === convId) { setExpandedId(null); setExpandedConv(null); }
    fetchConversations();
  };

  const toggleExpand = async (convId) => {
    if (expandedId === convId) { setExpandedId(null); setExpandedConv(null); return; }
    setExpandedId(convId);
    setLoadingConv(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/cfo-buck/conversations/${convId}`);
      if (res.ok) setExpandedConv(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingConv(false); }
  };

  const exportConv = (conv) => {
    if (!conv?.messages) return;
    const msgHtml = conv.messages.map(m => `
      <div style="margin-bottom:16px;display:flex;gap:12px;${m.role==='user'?'flex-direction:row-reverse':''}">
        <div style="width:32px;height:32px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:white;${m.role==='user'?'background:#059669':'background:linear-gradient(135deg,#8b5cf6,#4f46e5)'}">
          ${m.role==='user'?'You':'CFO'}
        </div>
        <div style="max-width:75%;padding:12px 16px;border-radius:16px;font-size:14px;line-height:1.6;${m.role==='user'?'background:#059669;color:white;border-bottom-right-radius:4px':'background:#f1f5f9;color:#334155;border-bottom-left-radius:4px'}">
          ${m.content.replace(/\n/g,'<br>')}
        </div>
      </div>`).join('');
    printContent(`
      <div style="max-width:700px;margin:0 auto">
        <h1 style="color:#0f172a;font-size:24px;margin:0 0 4px">${conv.name}</h1>
        <p style="color:#94a3b8;font-size:12px;margin:0 0 20px">Exported ${new Date().toLocaleDateString()} from CountBucks</p>
        <hr style="border:1px solid #e2e8f0;margin:0 0 20px">${msgHtml}
        <hr style="border:1px solid #e2e8f0;margin:20px 0">
        <p style="color:#94a3b8;font-size:11px;margin:0">Generated by CFO Buck - AI Financial Officer & Tax Strategist</p>
      </div>
    `, { filename: `${(conv.name || 'CFO_Buck_Chat').replace(/\s+/g, '_')}.pdf` });
  };

  // Collect all unique tags from conversations
  const allTags = [...new Set(conversations.flatMap(c => c.category_tags || []))];

  return (
    <div className="space-y-5" data-testid="saved-chats-tab">
      {/* Search & Filters */}
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..." className="pl-9 h-9 text-sm"
                data-testid="search-conversations" />
            </div>
            <span className="text-xs text-muted-foreground">{conversations.length} conversation{conversations.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => setFilterTag('all')} className={cn("px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all", filterTag === 'all' ? "bg-foreground text-background border-transparent" : "border-border text-muted-foreground")} data-testid="filter-all">All</button>
            {allTags.map(tag => (
              <button key={tag} onClick={() => setFilterTag(tag)} className={cn("px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all", filterTag === tag ? "bg-foreground text-background border-transparent" : "border-border text-muted-foreground")} data-testid={`filter-${tag}`}>
                {TAG_LABELS[tag] || tag}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Conversation List */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      ) : conversations.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-16 text-center">
          <FolderOpen className="w-10 h-10 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-base font-semibold text-foreground mb-2">{searchQuery || filterTag !== 'all' ? 'No matching conversations' : 'No saved conversations yet'}</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">{searchQuery || filterTag !== 'all' ? 'Try adjusting your search or filter.' : 'Start a chat in the "Ask CFO Buck" tab and save it to see it here.'}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {conversations.map((conv) => (
            <Card key={conv.conversation_id} className={cn("border transition-all hover:shadow-md", conv.pinned ? "border-violet-200 dark:border-violet-800 bg-violet-50/20 dark:bg-violet-900/5" : "border-border")} data-testid={`saved-conv-${conv.conversation_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500/10 to-indigo-600/10 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-4 h-4 text-violet-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {conv.pinned && <Pin className="w-3 h-3 text-violet-500 flex-shrink-0" />}
                      {editingId === conv.conversation_id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-7 text-sm flex-1"
                            autoFocus onKeyDown={(e) => { if (e.key === 'Enter') handleRename(conv.conversation_id); if (e.key === 'Escape') setEditingId(null); }}
                            data-testid="rename-input" />
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleRename(conv.conversation_id)}><Check className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingId(null)}><X className="w-3 h-3" /></Button>
                        </div>
                      ) : (
                        <h4 className="text-sm font-semibold text-foreground truncate">{conv.name}</h4>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {(conv.category_tags || []).map(tag => (
                        <Badge key={tag} variant="outline" className={cn("text-[9px] px-1.5 py-0 border-0 rounded-full", TAG_COLORS[tag] || TAG_COLORS.general)}>
                          {TAG_LABELS[tag] || tag}
                        </Badge>
                      ))}
                      <span className="text-[10px] text-muted-foreground">{conv.message_count} messages</span>
                      <span className="text-[10px] text-muted-foreground">{new Date(conv.updated_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button variant="outline" size="sm" className="h-7 text-[11px] px-2.5" onClick={() => onResume(conv)} data-testid={`resume-${conv.conversation_id}`}>
                        <ArrowRight className="w-3 h-3 mr-1" />Resume
                      </Button>
                      <Tip text="Expand to preview conversation"><span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => toggleExpand(conv.conversation_id)} data-testid={`preview-${conv.conversation_id}`}>
                        <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", expandedId === conv.conversation_id && "rotate-180")} />
                      </Button></span></Tip>
                      <Tip text="Rename this conversation"><span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditingId(conv.conversation_id); setEditName(conv.name); }} data-testid={`rename-${conv.conversation_id}`}>
                        <Edit3 className="w-3 h-3" />
                      </Button></span></Tip>
                      <Tip text={conv.pinned ? "Unpin from top" : "Pin to top of list"}><span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handlePin(conv.conversation_id, conv.pinned)} data-testid={`pin-${conv.conversation_id}`}>
                        {conv.pinned ? <PinOff className="w-3 h-3 text-violet-500" /> : <Pin className="w-3 h-3" />}
                      </Button></span></Tip>
                      {expandedConv?.conversation_id === conv.conversation_id && (
                        <Tip text="Export as printable PDF"><span>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => exportConv(expandedConv)} data-testid={`export-saved-${conv.conversation_id}`}>
                          <Download className="w-3 h-3" />
                        </Button></span></Tip>
                      )}
                      <Tip text="Permanently delete this conversation"><span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-500 hover:text-rose-700" onClick={() => handleDelete(conv.conversation_id)} data-testid={`delete-${conv.conversation_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button></span></Tip>
                    </div>
                  </div>
                </div>
                {/* Expanded preview */}
                {expandedId === conv.conversation_id && (
                  <div className="mt-4 pt-4 border-t border-border">
                    {loadingConv ? (
                      <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-violet-400" /></div>
                    ) : expandedConv?.messages ? (
                      <div className="max-h-[300px] overflow-y-auto space-y-3 pr-2">
                        {expandedConv.messages.map((msg, i) => (
                          <div key={i} className={cn("flex gap-2", msg.role === 'user' && "flex-row-reverse")}>
                            <div className={cn("w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white", msg.role === 'user' ? "bg-emerald-500" : "bg-gradient-to-br from-violet-500 to-indigo-600")}>
                              {msg.role === 'user' ? 'Y' : 'C'}
                            </div>
                            <div className={cn("max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed", msg.role === 'user' ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground")}>
                              {msg.content.split('\n').map((line, j) => <p key={j} className={j > 0 ? "mt-1" : ""}>{line}</p>)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-muted-foreground text-center py-4">Failed to load messages</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE ──────────────────────────────────────────────────────────────
export { ChatTab, SavedChatsTab };
